import { useCallback, useEffect, useState } from "react";
import {
  Mic, Music4, Waves, Wand2, Scissors, Loader2, Download, Play, ImagePlus,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErr, fileUrl } from "../lib/api";
import GenCard, { useGenerationPoll } from "../components/GenCard";
import { useAuth } from "../lib/AuthContext";
import { useI18n } from "../lib/i18n";

const inp =
  "w-full rounded-lg border border-[#212230] bg-[#101118] px-3 py-2.5 text-[12.5px] outline-none transition-colors focus:border-[#ff4b2b]/60";


const OPS = {
  en: [
    ["remove_object", "Remove object", "the parked car on the left"],
    ["replace_background", "Replace background", "a rainy Paris rooftop at night"],
    ["change_clothing", "Change clothing", "a black tailored trench coat"],
    ["change_lighting", "Relight scene", "golden hour, warm side key light"],
    ["recolor", "Recolour", "the dress, to deep emerald"],
    ["add_object", "Add object", "volumetric fog and light rays"],
    ["extend", "Extend frame", "wider, revealing the whole street"],
    ["upscale", "Restore & upscale", "remove noise, sharpen micro-detail"],
    ["custom", "Custom instruction", "describe exactly what to change"],
  ],
  fr: [
    ["remove_object", "Supprimer un élément", "la voiture garée à gauche"],
    ["replace_background", "Remplacer le décor", "un toit parisien sous la pluie, la nuit"],
    ["change_clothing", "Changer les vêtements", "un trench noir sur mesure"],
    ["change_lighting", "Rééclairer la scène", "golden hour, lumière chaude latérale"],
    ["recolor", "Recolorer", "la robe, en vert émeraude profond"],
    ["add_object", "Ajouter un élément", "brume volumétrique et rais de lumière"],
    ["extend", "Étendre le cadre", "plus large, révéler toute la rue"],
    ["upscale", "Restaurer & upscaler", "réduire le bruit, affiner le micro-détail"],
    ["custom", "Instruction libre", "décrivez exactement le changement"],
  ],
};

const TABS = {
  en: [["voice", "Voice", Mic], ["sound", "Music & SFX", Music4],
       ["retouch", "Retouch", Wand2], ["frames", "Frames", Scissors]],
  fr: [["voice", "Voix", Mic], ["sound", "Musique & SFX", Music4],
       ["retouch", "Retouche", Wand2], ["frames", "Frames", Scissors]],
};

const ENGINE_LABEL = {
  fr: {
    native: ["Natif", "Voix naturelles en français, espagnol, allemand, italien, japonais et 25+ langues."],
    openai: ["OpenAI", "Le plus rapide et le moins cher ; léger accent anglais hors anglais."],
    clone: ["Clonage", "Clonage zero-shot : donnez 5 à 30 s d'un enregistrement propre de la voix à copier."],
  },
  en: {
    native: ["Native", "Native-sounding French, Spanish, German, Italian, Japanese and 25+ more."],
    openai: ["OpenAI", "Fastest and cheapest; a light English accent on non-English text."],
    clone: ["Cloning", "Zero-shot cloning: give a 5-30 s clean sample of the voice to copy."],
  },
};

function Section({ title, sub, children }) {
  return (
    <div className="panel rounded-2xl p-6 lg:p-8">
      <h2 className="font-display text-lg font-medium md:text-lg">{title}</h2>
      {sub && <p className="mt-2 max-w-[620px] text-[12.5px] leading-relaxed text-[#9194a6]">{sub}</p>}
      <div className="mt-6 space-y-4">{children}</div>
    </div>
  );
}

function AudioRow({ a, locale }) {
  return (
    <div className="rounded-xl border border-[#212230] bg-[#0e0e15] p-3.5" data-testid={`audio-asset-${a.id}`}>
      <div className="flex items-center gap-3">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[#ff4b2b]/15">
          <Waves className="h-3.5 w-3.5 text-[#ff4b2b]" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[12.5px] font-medium">{a.name}</p>
          <p className="mono mt-0.5 truncate text-[9.5px] uppercase tracking-[0.14em] text-[#8288a0]">
            {a.voice ? `${a.voice} · ` : ""}{a.duration ? `${a.duration.toFixed(1)}s` : "audio"}
          </p>
        </div>
        <a href={fileUrl(a.id)} download className="btn-ghost grid h-7 w-7 shrink-0 place-items-center rounded-lg"
          title={locale === "fr" ? "Télécharger" : "Download"}>
          <Download className="h-3 w-3" />
        </a>
      </div>
      <audio src={fileUrl(a.id)} controls className="mt-3 h-8 w-full" data-testid={`audio-player-${a.id}`} />
    </div>
  );
}

export default function MediaStudio() {
  const { locale } = useI18n();
  const { refresh } = useAuth();
  const [tab, setTab] = useState("voice");
  const [busy, setBusy] = useState("");
  const [audios, setAudios] = useState([]);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);

  const [voice, setVoice] = useState({
    text: "", engine: "native", voice: "Charlotte", speed: 1, language: "fr",
    stability: 0.5, similarity_boost: 0.75, style: 0.2, clone_asset_id: "",
    name: "Voice-over",
  });
  const [engines, setEngines] = useState([]);
  const [samples, setSamples] = useState([]);
  const engine = engines.find((e) => e.id === voice.engine);
  const [sound, setSound] = useState({ prompt: "", kind: "music", duration: 20 });
  const [edit, setEdit] = useState({ asset_id: "", operation: "remove_object", target: "" });
  const [frame, setFrame] = useState({ asset_id: "", at_seconds: 0 });
  const [retouchPreview, setRetouchPreview] = useState(null);
  const [framePreview, setFramePreview] = useState(null);
  const [soundIds, setSoundIds] = useState([]);

  const load = useCallback(() => {
    api.get("/assets?kind=audio").then(({ data }) => setAudios(data)).catch(() => {});
    api.get("/assets?kind=image").then(({ data }) => setImages(data)).catch(() => {});
    api.get("/assets?kind=video").then(({ data }) => setVideos(data)).catch(() => {});
    api.get("/media/voices").then(({ data }) => {
      setEngines(data.engines || []);
      setSamples(data.clone_samples || []);
    }).catch(() => {});
  }, []);
  useEffect(load, [load]);
  const soundGens = useGenerationPoll(soundIds, load);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try {
      const res = await fn();
      toast.success(ok);
      load();
      refresh?.();
      return res;
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy("");
    }
  };

  const ops = OPS[locale];
  const currentOp = ops.find(([k]) => k === edit.operation);

  return (
    <div className="p-6 lg:p-10" data-testid="media-studio-page">
      <p className="overline">{locale === "fr" ? "Studio média" : "Media studio"}</p>
      <h1 className="mt-3 font-display text-3xl font-semibold sm:text-4xl">
        {locale === "fr" ? "Voix, musique, SFX & retouche" : "Voice, music, SFX & retouch"}
      </h1>
      <p className="mt-4 max-w-[680px] text-sm leading-relaxed text-[#9194a6]">
        {locale === "fr"
          ? "Composez la couche audio de votre film et retouchez n'importe quelle image avant de l'animer. Chaque opération affiche son coût en crédits et rembourse automatiquement en cas d'échec."
          : "Compose the audio bed of your film and retouch any frame before animating it. Every operation shows its credit cost and refunds automatically on failure."}
      </p>

      <div className="mt-8 flex flex-wrap gap-2">
        {TABS[locale].map(([k, label, Icon]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`media-tab-${k}`}
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[12px] font-semibold transition-colors ${
              tab === k ? "border-white bg-white text-black" : "border-[#2a2c3c] text-[#9194a6] hover:text-white"
            }`}
          >
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>

      <div className="mt-8 grid gap-6 xl:grid-cols-[minmax(0,1fr)_400px]">
        <div className="space-y-6">
          {tab === "voice" && (
            <Section
              title={locale === "fr" ? "Voix off, dialogue & clonage" : "Voice-over, dialogue & cloning"}
              sub={ENGINE_LABEL[locale][voice.engine]?.[1] || engine?.note}
            >
              <textarea
                value={voice.text}
                onChange={(e) => setVoice({ ...voice, text: e.target.value })}
                rows={6}
                data-testid="voice-text"
                placeholder={locale === "fr"
                  ? "« Nous devons partir maintenant. » — écrivez la narration ou le dialogue…"
                  : "\"We need to leave now.\" — write the narration or dialogue…"}
                className={inp}
              />
              <div className="grid gap-2 sm:grid-cols-3">
                {engines.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => setVoice({ ...voice, engine: e.id, voice: e.voices[0] })}
                    data-testid={`voice-engine-${e.id}`}
                    className={`rounded-lg border px-3 py-2.5 text-left transition-colors ${
                      voice.engine === e.id
                        ? "border-[#ff4b2b]/60 bg-[#ff4b2b]/[0.08]"
                        : "border-[#212230] hover:border-[#383b4d]"
                    }`}
                  >
                    <span className="mono text-[9.5px] uppercase tracking-[0.16em] text-[#ffc76b]">
                      {ENGINE_LABEL[locale][e.id]?.[0] || e.id}
                    </span>
                    <p className="mt-1 text-[12px] font-medium leading-tight">{e.label}</p>
                  </button>
                ))}
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <label className="block">
                  <span className="overline">{locale === "fr" ? "Voix" : "Voice"}</span>
                  <select value={voice.voice} onChange={(e) => setVoice({ ...voice, voice: e.target.value })} data-testid="voice-select" className={`${inp} mt-2`}>
                    {(engine?.voices || []).map((v) => <option key={v} value={v} children={v} />)}
                  </select>
                </label>
                <label className="block">
                  <span className="overline">{locale === "fr" ? "Langue" : "Language"}</span>
                  <select value={voice.language} onChange={(e) => setVoice({ ...voice, language: e.target.value })} data-testid="voice-language" className={`${inp} mt-2`}>
                    {(engine?.languages || []).map(([c, l]) => <option key={c} value={c} children={l} />)}
                  </select>
                </label>
                <label className="block">
                  <span className="overline">{locale === "fr" ? "Vitesse" : "Speed"} · {voice.speed}x</span>
                  <input type="range" min="0.7" max="1.2" step="0.05" value={voice.speed}
                    onChange={(e) => setVoice({ ...voice, speed: parseFloat(e.target.value) })}
                    data-testid="voice-speed" className="mt-4 w-full accent-[#ff4b2b]" />
                </label>
              </div>
              {voice.engine === "clone" && (
                <label className="block">
                  <span className="overline">
                    {locale === "fr" ? "Échantillon de voix à cloner (5–30 s)" : "Voice sample to clone (5–30 s)"}
                  </span>
                  <select value={voice.clone_asset_id} onChange={(e) => setVoice({ ...voice, clone_asset_id: e.target.value })} data-testid="voice-clone-sample" className={`${inp} mt-2`}>
                    <option value="" children={locale === "fr" ? "Choisir un échantillon…" : "Choose a sample…"} />
                    {samples.map((s) => (
                      <option key={s.id} value={s.id}
                        children={`${s.name}${s.duration ? ` — ${s.duration.toFixed(1)}s` : ""}`} />
                    ))}
                  </select>
                  <span className="mt-2 block text-[11.5px] leading-relaxed text-[#8288a0]">
                    {locale === "fr"
                      ? "Générez d'abord une voix, ou déposez un enregistrement propre dans la bibliothèque : le moteur copiera son timbre."
                      : "Generate a voice first, or drop a clean recording into the library — the engine copies its timbre."}
                  </span>
                </label>
              )}
              {voice.engine === "native" && (
                <div className="grid gap-3 sm:grid-cols-3">
                  {[["stability", locale === "fr" ? "Stabilité" : "Stability", 0.5],
                    ["similarity_boost", locale === "fr" ? "Similarité" : "Similarity", 0.75],
                    ["style", locale === "fr" ? "Style" : "Style", 0.2]].map(([k, label, dflt]) => (
                    <label key={k} className="block">
                      <span className="overline">{label} · {(voice[k] ?? dflt).toFixed(2)}</span>
                      <input type="range" min="0" max="1" step="0.05" value={voice[k] ?? dflt}
                        onChange={(e) => setVoice({ ...voice, [k]: parseFloat(e.target.value) })}
                        data-testid={`voice-${k}`} className="mt-4 w-full accent-[#00d5ff]" />
                    </label>
                  ))}
                </div>
              )}
              <button
                disabled={busy === "voice" || !voice.text.trim() ||
                          (voice.engine === "clone" && !voice.clone_asset_id)}
                onClick={() => run("voice", async () => {
                  const { data } = await api.post("/media/voice", { ...voice, quality: "standard" });
                  if (data.queued) setSoundIds((s) => [data.id, ...s].slice(0, 6));
                  return data;
                }, locale === "fr" ? "Voix générée" : "Voice generated")}
                data-testid="voice-generate"
                className="btn-amber inline-flex items-center gap-2 rounded-full px-6 py-3 text-[13px] font-semibold disabled:opacity-40"
              >
                {busy === "voice" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}
                {locale === "fr" ? "Générer la voix" : "Generate voice"}
              </button>
              {soundIds.length > 0 && (
                <div className="grid gap-3 sm:grid-cols-2" data-testid="voice-queue">
                  {soundIds.map((id) => <GenCard key={id} gen={soundGens[id]} />)}
                </div>
              )}
            </Section>
          )}

          {tab === "sound" && (
            <Section
              title={locale === "fr" ? "Musique & design sonore" : "Music & sound design"}
              sub={locale === "fr"
                ? "Décrivez l'ambiance : le moteur audio compose une piste à la durée demandée."
                : "Describe the mood: the audio engine composes a track at the requested length."}
            >
              <textarea
                value={sound.prompt}
                onChange={(e) => setSound({ ...sound, prompt: e.target.value })}
                rows={4}
                data-testid="sound-prompt"
                placeholder={locale === "fr"
                  ? "Score orchestral tendu, cordes graves, pluie et tonnerre lointain…"
                  : "Tense orchestral score, low strings, rain and distant thunder…"}
                className={inp}
              />
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="overline">Type</span>
                  <select value={sound.kind} onChange={(e) => setSound({ ...sound, kind: e.target.value })} data-testid="sound-kind" className={`${inp} mt-2`}>
                    <option value="music" children={locale === "fr" ? "Musique" : "Music"} />
                    <option value="sfx" children={locale === "fr" ? "SFX / ambiance" : "SFX / ambience"} />
                  </select>
                </label>
                <label className="block">
                  <span className="overline">{locale === "fr" ? "Durée" : "Duration"} · {sound.duration}s</span>
                  <input type="range" min="5" max="120" step="5" value={sound.duration}
                    onChange={(e) => setSound({ ...sound, duration: parseInt(e.target.value, 10) })}
                    data-testid="sound-duration" className="mt-4 w-full accent-[#00d5ff]" />
                </label>
              </div>
              <button
                disabled={busy === "sound" || !sound.prompt.trim()}
                onClick={() => run("sound", async () => {
                  const { data } = await api.post("/media/sound", sound);
                  setSoundIds((s) => [data.id, ...s].slice(0, 6));
                  return data;
                }, locale === "fr" ? "Génération audio lancée" : "Audio generation queued")}
                data-testid="sound-generate"
                className="btn-amber inline-flex items-center gap-2 rounded-full px-6 py-3 text-[13px] font-semibold disabled:opacity-40"
              >
                {busy === "sound" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Music4 className="h-4 w-4" />}
                {locale === "fr" ? "Composer" : "Compose"}
              </button>
              {soundIds.length > 0 && (
                <div className="grid gap-3 sm:grid-cols-2" data-testid="sound-queue">
                  {soundIds.map((id) => <GenCard key={id} gen={soundGens[id]} />)}
                </div>
              )}
            </Section>
          )}

          {tab === "retouch" && (
            <Section
              title={locale === "fr" ? "Suite de retouche d'image" : "Image retouch suite"}
              sub={locale === "fr"
                ? "Modifiez une image avant de l'animer : suppression d'objet, décor, vêtements, lumière, extension, upscale."
                : "Edit any frame before animating it: object removal, background, wardrobe, lighting, extension, upscale."}
            >
              <label className="block">
                <span className="overline">{locale === "fr" ? "Image source" : "Source frame"}</span>
                <select value={edit.asset_id} onChange={(e) => setEdit({ ...edit, asset_id: e.target.value })} data-testid="retouch-asset" className={`${inp} mt-2`}>
                  <option value="" children={locale === "fr" ? "Choisir une image…" : "Choose an image…"} />
                  {images.map((a) => <option key={a.id} value={a.id} children={a.name} />)}
                </select>
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="overline">{locale === "fr" ? "Opération" : "Operation"}</span>
                  <select value={edit.operation} onChange={(e) => setEdit({ ...edit, operation: e.target.value })} data-testid="retouch-operation" className={`${inp} mt-2`}>
                    {ops.map(([k, label]) => <option key={k} value={k} children={label} />)}
                  </select>
                </label>
                <label className="block">
                  <span className="overline">{locale === "fr" ? "Cible / détail" : "Target / detail"}</span>
                  <input value={edit.target} onChange={(e) => setEdit({ ...edit, target: e.target.value })}
                    placeholder={currentOp?.[2]} data-testid="retouch-target" className={`${inp} mt-2`} />
                </label>
              </div>
              <button
                disabled={busy === "retouch" || !edit.asset_id}
                onClick={() => {
                  setRetouchPreview(null);
                  run("retouch", async () => {
                    const { data } = await api.post("/media/image-edit", edit);
                    setRetouchPreview(data);
                    return data;
                  }, locale === "fr" ? "Retouche appliquée" : "Retouch applied");
                }}
                data-testid="retouch-run"
                className="btn-amber inline-flex items-center gap-2 rounded-full px-6 py-3 text-[13px] font-semibold disabled:opacity-40"
              >
                {busy === "retouch" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                {locale === "fr" ? "Appliquer" : "Apply"}
              </button>
              {retouchPreview && (
                <div className="flash-in overflow-hidden rounded-xl border border-[#212230]">
                  <img src={fileUrl(retouchPreview.id)} alt="" className="w-full object-cover" data-testid="retouch-preview" />
                </div>
              )}
            </Section>
          )}

          {tab === "frames" && (
            <Section
              title={locale === "fr" ? "Extraction de frame" : "Frame extraction"}
              sub={locale === "fr"
                ? "Extrayez n'importe quelle image d'un plan généré, retouchez-la, puis réanimez-la : vidéo → image → vidéo."
                : "Pull any frame out of a generated shot, retouch it, then re-animate it: video → image → video."}
            >
              <label className="block">
                <span className="overline">{locale === "fr" ? "Plan source" : "Source shot"}</span>
                <select value={frame.asset_id} onChange={(e) => setFrame({ ...frame, asset_id: e.target.value })} data-testid="frame-asset" className={`${inp} mt-2`}>
                  <option value="" children={locale === "fr" ? "Choisir une vidéo…" : "Choose a video…"} />
                  {videos.map((a) => <option key={a.id} value={a.id} children={a.name} />)}
                </select>
              </label>
              {frame.asset_id && (
                <video src={fileUrl(frame.asset_id)} controls className="w-full rounded-xl border border-[#212230]" data-testid="frame-source-player" />
              )}
              <label className="block">
                <span className="overline">Timecode · {frame.at_seconds.toFixed(1)}s</span>
                <input type="range" min="0" max="30" step="0.1" value={frame.at_seconds}
                  onChange={(e) => setFrame({ ...frame, at_seconds: parseFloat(e.target.value) })}
                  data-testid="frame-time" className="mt-4 w-full accent-[#00d5ff]" />
              </label>
              <button
                disabled={busy === "frames" || !frame.asset_id}
                onClick={() => {
                  setFramePreview(null);
                  run("frames", async () => {
                    const { data } = await api.post("/media/extract-frame", frame);
                    setFramePreview(data);
                    return data;
                  }, locale === "fr" ? "Frame extraite" : "Frame extracted");
                }}
                data-testid="frame-extract"
                className="btn-amber inline-flex items-center gap-2 rounded-full px-6 py-3 text-[13px] font-semibold disabled:opacity-40"
              >
                {busy === "frames" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                {locale === "fr" ? "Extraire la frame" : "Extract frame"}
              </button>
              {framePreview && (
                <div className="flash-in overflow-hidden rounded-xl border border-[#212230]">
                  <img src={fileUrl(framePreview.id)} alt="" className="w-full object-cover" data-testid="frame-preview" />
                </div>
              )}
            </Section>
          )}
        </div>

        <aside className="space-y-4">
          <div className="panel rounded-2xl p-5">
            <p className="overline">{locale === "fr" ? "Bibliothèque audio" : "Audio library"}</p>
            <div className="mt-4 space-y-3">
              {audios.length === 0 && (
                <p className="text-[12.5px] text-[#8288a0]">
                  {locale === "fr" ? "Aucune piste pour l'instant." : "No tracks yet."}
                </p>
              )}
              {audios.slice(0, 12).map((a) => <AudioRow key={a.id} a={a} locale={locale} />)}
            </div>
          </div>

          <div className="panel rounded-2xl p-5">
            <p className="overline">{locale === "fr" ? "Frames récentes" : "Recent frames"}</p>
            <div className="mt-4 grid grid-cols-3 gap-2">
              {images.slice(0, 9).map((a) => (
                <button key={a.id} onClick={() => { setEdit({ ...edit, asset_id: a.id }); setTab("retouch"); }}
                  data-testid={`frame-thumb-${a.id}`}
                  className="lift overflow-hidden rounded-lg border border-[#212230]">
                  <img src={fileUrl(a.id)} alt={a.name} className="h-16 w-full object-cover" loading="lazy" />
                </button>
              ))}
              {images.length === 0 && (
                <p className="col-span-3 text-[12.5px] text-[#8288a0]">
                  {locale === "fr" ? "Générez un storyboard pour commencer." : "Generate a storyboard to get started."}
                </p>
              )}
            </div>
          </div>

          <div className="panel rounded-2xl p-5">
            <p className="overline">{locale === "fr" ? "Astuce production" : "Production tip"}</p>
            <p className="mt-3 flex gap-2.5 text-[12.5px] leading-relaxed text-[#9194a6]">
              <Play className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#00d5ff]" />
              {locale === "fr"
                ? "Les moteurs Cinema 2.5 et Engine V produisent déjà l'audio synchronisé (ambiance, effets, dialogue lip-sync) directement dans le plan. Utilisez cette page pour la narration, la musique et les couches SFX supplémentaires."
                : "The Cinema 2.5 and Engine V motion models already render synchronised audio (ambience, effects, lip-synced dialogue) inside the shot. Use this page for narration, score and extra SFX layers."}
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
